import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { invoiceSchema, projectSchema, type Invoice, type Project } from '@forecastify/shared';
import { Button, Icon } from '@forecastify/ui';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { useGoBack, useWorkspaceNavigate } from '../../workspace-path';
import { Breadcrumb } from '../breadcrumb';
import { usePageHeader } from '../page-header';
import { parseList, usePermissionSet } from '../permissions-hook';
import { ScreenState } from '../screen-state';
import { invoiceCrumbs } from './money-crumbs';
import { InvoiceList } from './invoice-list';
import { InvoiceDetail } from './invoice-detail';
import { InvoiceDialog, draftOf, type InvoiceDraft } from './invoice-dialog';
import { useInvoices, type InvoicePatch } from './use-invoices';
import { requestAll } from '../read-all';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C57 · `/invoices` and `/invoices/:number`.                                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Keyed on OUR identifier (`inv_21b5a1e2`, R-57), not the id and not theirs.** It is
 * generated and unique, so it cannot collide; the older `SZ-5011` form still resolves and is
 * rewritten in place. The external number is the one on the screen but it is not unique: a
 * corrected re-issue after a `void` legitimately carries the same one, which `0134` records.
 *
 * ⚠️⚠️ **THE PAGE KEEPS ITS OWN MARKER EVEN WHEN THE NUMBER IS UNKNOWN**, which is the lesson
 * `/contracts/:code` learned: a surface that only exists when something matched makes a bookmark to
 * a deleted row silently become the list, at an address that says otherwise.
 */
export function InvoicesRoute({ page = false }: { readonly page?: boolean } = {}) {
  const t = useT();
  const go = useWorkspaceNavigate();
  const session = useSession();
  const params = useParams();
  const permissions = usePermissionSet();
  const data = useInvoices();

  const [projects, setProjects] = useState<readonly Project[]>([]);
  const [editing, setEditing] = useState<InvoiceDraft | null>(null);
  /* ux-pass-2 · the detail opens the dialog for ONE question — what the invoice bills. */
  const [focus, setFocus] = useState<'target' | undefined>(undefined);
  const goBack = useGoBack();
  const [periods, setPeriods] = useState<readonly { startsOn: string; label: string }[]>([]);

  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      const response = await requestAll(session, '/projects').catch(() => null);
      if (response?.ok !== true) return;
      setProjects(parseList(projectSchema, await response.json()) ?? []);
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug). */
  }, [session.workspaceId]);

  /* ⚠️ The periods of whichever contract the dialog points at, read when the pick changes — a
     workspace with ten frameworks would otherwise fetch two hundred to fill a dropdown. */
  const pickedContract = editing?.contractId ?? '';
  useEffect(() => {
    void (async () => {
      setPeriods(await data.periodsOf(pickedContract));
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- see above. */
  }, [pickedContract]);

  const canManage = permissions.has('contract.manage');

  /**
   * R-57 · the identifier is the address; `SZ-5011` (or a bare number) still resolves — on the
   * server now (`GET /invoices/:ref`). 31 Oct 2026 · the page read every invoice in the workspace
   * to find this one. Re-read on `revision`, so a write or a colleague's change lands here.
   */
  const ref = page ? (params.number ?? '') : '';
  const [open, setOpen] = useState<Invoice | null>(null);
  const [resolving, setResolving] = useState(page);
  const workspaceId = session.workspaceId;
  const sessionRef = useRef(session);
  sessionRef.current = session;
  useEffect(() => {
    if (ref === '' || workspaceId === null) return;
    const run = { live: true };
    void (async () => {
      const response = await sessionRef.current
        .request(`/invoices/${encodeURIComponent(ref)}`)
        .catch(() => null);
      const body: unknown = response?.ok === true ? await response.json().catch(() => null) : null;
      if (!run.live) return;
      /* ⚠️ A refusal is *unknown invoice* only when it says so — a failed read keeps what is open. */
      if (response?.status === 404) setOpen(null);
      else if (body !== null) setOpen(invoiceSchema.safeParse(body).data ?? null);
      setResolving(false);
    })();
    return () => {
      run.live = false;
    };
  }, [ref, workspaceId, data.revision]);

  /* R-57 · resolved from the old number, so the bar is rewritten to the identifier. */
  useEffect(() => {
    if (open === null || params.number === open.identifier) return;
    go(`/invoices/${open.identifier}`, { replace: true });
  }, [open, params.number, go]);

  /**
   * ⚠️⚠️ **THE PAGE CLAIMS THE SHELL'S HEADER, WITH A TRAIL — ux-pass-2, 14 Oct.** *"Also
   * breadcrumb is missing in both places, fix it."* It drew no header at all, so the shell fell
   * back to the route's title and the page said nothing about where the invoice lives. The trail
   * is `money-crumbs.ts`'s, and the chevron returns where the reader came from (`useGoBack`) —
   * the list only when there is nothing behind this page.
   *
   * ⚠️ Inert on the list (`page === false`), whose own screen claims the header.
   */
  usePageHeader(
    () =>
      open === null ? null : (
        <>
          <Button
            variant="ghost"
            size="icon-sm"
            className="-ml-1.5"
            aria-label={t('task.action.back')}
            onClick={() => {
              goBack('/invoices');
            }}
          >
            <Icon name="chevron-left" size={16} />
          </Button>
          <Breadcrumb copyValue={open.identifier} crumbs={invoiceCrumbs(open, t, go)} />
        </>
      ),
    /* ⚠️ VALUES, NOT CALLBACKS — `hard-won.md` C24-1. */
    [
      open?.identifier,
      open?.externalId,
      open?.projectName,
      open?.contractIdentifier,
      open?.lineItemName,
      open?.occurrenceIndex,
      open?.targetKind,
    ],
    /* Until it resolves, the shell's route title stands. */
    page && open !== null,
  );

  const dialog =
    editing === null ? null : (
      <InvoiceDialog
        draft={editing}
        lineItems={data.lineItems}
        contracts={data.contracts}
        periods={periods}
        members={data.members}
        remainingLabel=""
        serverError={data.serverError}
        {...(focus === undefined ? {} : { focus })}
        onCancel={() => {
          setEditing(null);
          setFocus(undefined);
          data.clearError();
        }}
        onSave={(body, closeSource) => {
          void (async () => {
            const saved = await data.save(editing.id, body, closeSource);
            if (saved !== null) {
              setEditing(null);
              setFocus(undefined);
            }
          })();
        }}
      />
    );

  if (page) {
    return (
      <ScreenState failure={null} onRetry={() => void data.reload()}>
        <div data-screen="invoice" className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {open === null ? (
            <p className="text-muted-foreground p-5 text-[12.5px]">
              {resolving ? t('state.loading') : t('invoice.unknown')}
            </p>
          ) : (
            <div className="mx-auto flex min-h-0 w-full max-w-[1160px] flex-1 flex-col">
              {data.serverError === null ? null : (
                <p className="text-destructive px-5 pt-3 text-[12px]">{data.serverError}</p>
              )}
              <InvoiceDetail
                wide
                data={{
                  invoice: open,
                  metafields: data.metafields,
                  members: data.members,
                  currencies: data.currencies,
                }}
                handlers={detailHandlers(open)}
              />
            </div>
          )}
        </div>
        {dialog}
      </ScreenState>
    );
  }

  return (
    <ScreenState failure={null} onRetry={() => void data.reload()}>
      <div data-screen="invoices" className="flex min-h-0 flex-1 flex-col">
        {data.serverError === null ? null : (
          <p className="text-destructive px-4 pt-2 text-[12px]">{data.serverError}</p>
        )}
        <InvoiceList
          nonce={data.revision}
          currencies={data.currencies}
          projects={projects.map((one) => ({ id: one.id, name: one.name }))}
          handlers={{
            canManage,
            onOpen: (invoice) => {
              go(`/invoices/${invoice.identifier}`);
            },
            onEdit: (invoice) => {
              setFocus(undefined);
              setEditing(draftOf(invoice));
            },
            onStatus: (invoice, status) => {
              void data.setStatus(invoice, status);
            },
            onDelete: (invoice) => {
              void data.remove(invoice);
            },
            /* ⚠️ **No *new invoice* here, and the empty state says why.** An invoice is raised on
               the deliverable it bills — the mockup's own sentence — and a form starting from
               nothing would ask somebody to find a line item by name in a workspace-wide list. */
          }}
        />
      </div>
      {dialog}
    </ScreenState>
  );

  function detailHandlers(invoice: Invoice) {
    return {
      /* 22 Oct 2026 · the server's answer for this invoice's project (`project-reach.ts`). */
      canManage: canManage && invoice.canManage,
      onStatus: (status: Parameters<typeof data.setStatus>[1]) => {
        void data.setStatus(invoice, status);
      },
      onPatch: (change: InvoicePatch) => {
        void data.patch(invoice, change);
      },
      onEditTarget: () => {
        setFocus('target');
        setEditing(draftOf(invoice));
      },
      onDelete: () => {
        void (async () => {
          await data.remove(invoice);
          go('/invoices');
        })();
      },
      onMetafields: (values: Record<string, unknown>) => {
        void data.patch(invoice, { metafields: values });
      },
      onOpenProject: () => {
        go(`/projects/${invoice.projectId}`);
      },
      onOpenTarget: () => {
        if (invoice.targetKind === 'line_item') {
          /* R-57 · a line item's address is its contract's identifier AND its own. */
          if (invoice.contractIdentifier !== null && invoice.lineItemIdentifier !== null) {
            go(`/contracts/${invoice.contractIdentifier}/${invoice.lineItemIdentifier}`);
          }
        } else if (invoice.contractIdentifier !== null && invoice.occurrenceStartsOn !== null) {
          go(`/contracts/${invoice.contractIdentifier}/periods/${invoice.occurrenceStartsOn}`);
        }
      },
      onAttach: async (file: File): Promise<string | null> => {
        const form = new FormData();
        form.set('file', file);
        const response = await session
          .request(`/files?target=invoice&targetId=${invoice.id}`, { method: 'POST', body: form })
          .catch(() => null);
        return response?.ok === true ? null : 'upload_failed';
      },
    };
  }
}

/** `/invoices/:number` — one invoice, at an address somebody can send. */
export function InvoicePageRoute() {
  return <InvoicesRoute page />;
}
